import * as React from 'react';

import { cn } from '../../lib/utils';
import { classeMontant, estNombre, estNegatif, libelleColonne, libelleGroupe, nombre } from './reportHelpers';

/**
 * Briques des tableaux de rapport: cadre défilable, en-têtes de groupe
 * discrets, lignes à rayures légères, première colonne collante, totaux en
 * gras sous une bordure haute, montants alignés à droite (négatifs en rouge,
 * résultats signés en vert ou en rouge) et pastille « Prévu » pour les
 * montants estimés.
 *
 * Le tableau défile horizontalement dans son cadre: sur un téléphone, la page
 * ne déborde jamais, et la première colonne reste visible.
 */

export const ALIGNEMENTS = {
  left: 'text-left',
  center: 'text-center',
  right: 'text-right tabular-nums',
};

const alignement = (align) => ALIGNEMENTS[align] || ALIGNEMENTS.left;

/** La première cellule de chaque ligne reste collée à gauche pendant le défilement. */
const COLLANTE = '[&>td:first-child]:sticky [&>td:first-child]:left-0 [&>td:first-child]:z-[1]';

const TONS_LIGNES = {
  normale: cn(
    'border-b border-border/60 transition-colors even:bg-muted/20 hover:bg-muted/50',
    COLLANTE,
    '[&>td:first-child]:bg-card hover:[&>td:first-child]:bg-muted',
  ),
  sousTotal: cn(
    'border-t border-border bg-muted/40 font-semibold',
    COLLANTE,
    '[&>td:first-child]:bg-muted',
  ),
  total: cn(
    'border-t-2 border-foreground/25 bg-card font-bold',
    COLLANTE,
    '[&>td:first-child]:bg-card',
  ),
};

/** Cadre défilable d'un tableau. `titre` est le nom du groupe (« Propres »). */
export const CadreTableau = ({ titre, detail, note, children, className }) => (
  <section className={cn('space-y-2', className)}>
    {(titre || detail) && (
      <div className="flex flex-wrap items-baseline justify-between gap-x-4 gap-y-1">
        {titre && <h3 className="text-sm font-semibold text-foreground">{titre}</h3>}
        {detail && <p className="text-xs text-muted-foreground">{detail}</p>}
      </div>
    )}
    <div className="overflow-x-auto rounded-xl border border-border bg-card shadow-sm">{children}</div>
    {note && <p className="text-xs text-muted-foreground">{note}</p>}
  </section>
);

export const Tableau = ({ className, ...props }) => (
  <table className={cn('w-full border-collapse text-sm', className)} {...props} />
);

/** Cellule d'en-tête. `groupe`: ligne de regroupement (Recettes, Charges…), plus discrète. */
export const EnTete = ({ align = 'left', groupe = false, className, ...props }) => (
  <th
    scope={groupe ? 'colgroup' : 'col'}
    className={cn(
      'whitespace-nowrap px-3 py-2 text-xs',
      groupe
        ? 'border-x border-border/60 bg-muted/50 text-[11px] font-semibold uppercase tracking-wider text-muted-foreground first:border-l-0 last:border-r-0'
        : 'border-b border-border bg-muted/30 font-medium text-foreground',
      align === 'right' ? 'text-right' : align === 'center' ? 'text-center' : 'text-left',
      className,
    )}
    {...props}
  />
);

/** Ligne du corps. `ton`: `normale`, `sousTotal` ou `total`. */
export const Ligne = ({ ton = 'normale', className, ...props }) => (
  <tr className={cn(TONS_LIGNES[ton] || TONS_LIGNES.normale, className)} {...props} />
);

/** Cellule du corps. Les montants se calent à droite et passent au rouge s'ils sont négatifs. */
export const Case = ({ align = 'left', valeur, montant = false, fort = false, className, children, ...props }) => (
  <td
    className={cn(
      'whitespace-nowrap px-3 py-2 align-middle',
      alignement(align),
      montant && classeMontant(valeur),
      fort && 'font-semibold',
      className,
    )}
    {...props}
  >
    {children}
  </td>
);

/** Pastille discrète signalant un montant estimé, pas encore payé. */
export const PastillePrevu = ({ className, title = 'Montant prévu, pas encore payé' }) => (
  <span
    className={cn(
      'inline-flex items-center rounded-full border border-flame/30 bg-flame/10 px-1.5 py-px text-[10px] font-medium leading-4 text-flame-ink',
      className,
    )}
    title={title}
  >
    Prévu
  </span>
);

/** Montant avec son signe explicite: « + 1 215 000 » en vert, « − 475 000 » en rouge. */
export const formaterResultat = (valeur) => {
  if (!estNombre(valeur)) return '';
  const n = Number(valeur);
  if (n === 0) return '0';
  return `${n > 0 ? '+' : '−'}\u00a0${nombre(Math.abs(n))}`;
};

/**
 * Montant en cellule. Sur une ligne de détail, zéro s'affiche vide; sur un
 * total, zéro reste visible. `resultat`: signe explicite, vert ou rouge.
 * `prevu`: ajoute la pastille « Prévu ».
 */
export const MontantCase = ({ valeur, total = false, fort = false, resultat = false, prevu = false, titrePrevu, className, ...props }) => {
  const visible = total || Number(valeur) !== 0;
  const couleur = resultat && estNombre(valeur) && Number(valeur) > 0 ? 'text-success' : undefined;
  return (
    <Case align="right" valeur={valeur} montant fort={fort} className={cn(couleur, className)} {...props}>
      {prevu && <PastillePrevu className="mr-2" title={titrePrevu} />}
      {visible ? (resultat ? formaterResultat(valeur) : nombre(valeur)) : ''}
    </Case>
  );
};

/** Carte de chiffre du résumé en tête de page (chiffre petit, couleur = état). */
export const CarteChiffre = ({ label, valeur, unite, ton, detail, ...props }) => {
  const negatif = estNegatif(valeur);
  const couleur = ton === 'resultat' ? (negatif ? 'text-destructive' : Number(valeur) > 0 ? 'text-success' : '') : '';
  return (
    <div className="min-w-0 rounded-xl border border-border bg-card p-3.5 shadow-sm" {...props}>
      <p className="readout-label">{label}</p>
      <p className={cn('kpi-value mt-2', couleur)}>
        {ton === 'resultat' ? formaterResultat(valeur) || '0' : nombre(valeur) || '0'}
        {unite && <span className="ml-1.5 text-xs font-medium text-muted-foreground">{unite}</span>}
      </p>
      {detail && <p className="mt-1.5 text-xs text-muted-foreground">{detail}</p>}
    </div>
  );
};

/* ------------------------------------------------------------------------ */
/* En-têtes groupés                                                          */
/* ------------------------------------------------------------------------ */

/** Nombre de niveaux d'en-tête au-dessus du libellé d'une colonne. */
const profondeur = (colonne) => (colonne.group ? (colonne.subgroup ? 2 : 1) : 0);

/**
 * Construit les lignes d'en-tête à partir des colonnes de la réponse: les colonnes
 * voisines d'un même groupe se fusionnent, une colonne sans groupe garde son
 * en-tête sur toute la hauteur. Renvoie une liste de lignes de cellules
 * `{ cle, texte, colSpan, rowSpan, align, groupe }`.
 */
export const construireEntetes = (colonnes) => {
  const niveauMax = Math.max(0, ...colonnes.map(profondeur));
  const hauteur = niveauMax + 1;
  const cleNiveau = (colonne, niveau) =>
    niveau === 0 ? colonne.group : `${colonne.group}||${colonne.subgroup}`;

  const lignes = [];
  for (let r = 0; r < hauteur; r += 1) {
    const cellules = [];
    let i = 0;
    while (i < colonnes.length) {
      const colonne = colonnes[i];
      const d = profondeur(colonne);
      if (r < d) {
        const cle = cleNiveau(colonne, r);
        let etendue = 1;
        while (
          i + etendue < colonnes.length &&
          profondeur(colonnes[i + etendue]) > r &&
          cleNiveau(colonnes[i + etendue], r) === cle
        ) {
          etendue += 1;
        }
        cellules.push({
          cle: `g${r}-${i}`,
          texte: r === 0 ? libelleGroupe(colonne.group) : libelleGroupe(colonne.subgroup),
          colSpan: etendue,
          rowSpan: 1,
          align: 'center',
          groupe: true,
        });
        i += etendue;
      } else {
        if (r === d) {
          cellules.push({
            cle: `c${r}-${i}`,
            texte: libelleColonne(colonne),
            colSpan: 1,
            rowSpan: hauteur - d,
            align: colonne.align,
            groupe: false,
          });
        }
        i += 1;
      }
    }
    lignes.push(cellules);
  }
  return lignes;
};

/** `<thead>` à partir des colonnes (groupes et sous-groupes fusionnés). */
export const EntetesGroupes = ({ colonnes }) => {
  const lignes = React.useMemo(() => construireEntetes(colonnes), [colonnes]);
  return (
    <thead>
      {lignes.map((cellules, index) => (
        <tr key={index}>
          {cellules.map((cellule) => (
            <EnTete
              key={cellule.cle}
              colSpan={cellule.colSpan}
              rowSpan={cellule.rowSpan}
              align={cellule.align}
              groupe={cellule.groupe}
            >
              {cellule.texte}
            </EnTete>
          ))}
        </tr>
      ))}
    </thead>
  );
};
